<script setup lang="ts">
import type { TableColumn } from "@nuxt/ui"
import { computed } from "vue"
import type { DashboardSession } from "../utils/dashboard-types"
import { formatFutureRelative, formatRelative, formatTimestamp } from "../utils/format"
import ClippedValue from "./ClippedValue.vue"
import CredentialId from "./CredentialId.vue"
import NullBadge from "./NullBadge.vue"
import SessionId from "./SessionId.vue"
import SortableTable from "./SortableTable.vue"
import StatusBadge from "./StatusBadge.vue"

type TableSorting = Array<{ id: string; desc: boolean }>

const props = defineProps<{
  now: number
  sessions: DashboardSession[]
  sorting: TableSorting
  scrollTestId?: string
  modalScrollTestId?: string
}>()

const emit = defineEmits<{
  "update:sorting": [value: TableSorting]
}>()

const tableSorting = computed({
  get: () => props.sorting,
  set: (value: TableSorting) => emit("update:sorting", value)
})

const sessionColumns: TableColumn<DashboardSession>[] = [
  {
    accessorKey: "id",
    header: "Session",
    meta: {
      class: {
        td: "min-w-[150px]"
      }
    }
  },
  {
    id: "status",
    accessorFn: (session) => sessionStatus(session),
    header: "Status"
  },
  {
    accessorKey: "appId",
    header: "App",
    meta: {
      class: {
        td: "dashboard-table-cell--app min-w-[180px]"
      }
    }
  },
  {
    accessorKey: "requestedCountry",
    header: "Country"
  },
  {
    accessorKey: "requestedProfile",
    header: "Profile"
  },
  {
    accessorKey: "activeCredentialKid",
    header: "Credential"
  },
  {
    accessorKey: "credentialExpiresAt",
    header: "Expires"
  },
  {
    accessorKey: "lastSeenAt",
    header: "Last seen"
  },
  {
    accessorKey: "strict",
    header: "Strict"
  },
  {
    accessorKey: "stickyHashPresent",
    header: "Sticky"
  }
]

function sessionStatus(session: DashboardSession): string {
  return session.closedAt || session.status === "closed" || credentialExpired(session) ? "closed" : "active"
}

function hasCredentialExpiry(session: DashboardSession): boolean {
  return session.credentialExpiresAt !== null && session.credentialExpiresAt > 0
}

function credentialExpiryTooltip(session: DashboardSession): string {
  return session.credentialExpiresAt === null ? "" : formatTimestamp(session.credentialExpiresAt)
}

function credentialExpiryLabel(session: DashboardSession): string {
  if (session.credentialExpiresAt === null) {
    return ""
  }

  if (session.credentialExpiresAt <= props.now) {
    return "EXPIRED"
  }

  return formatFutureRelative(session.credentialExpiresAt, props.now)
}

function credentialExpired(session: DashboardSession): boolean {
  return session.credentialExpiresAt !== null && session.credentialExpiresAt <= props.now
}
</script>

<template>
  <SortableTable
    v-model:sorting="tableSorting"
    :columns="sessionColumns"
    :data="sessions"
    title="Session inventory"
    eyebrow="Bootstrap and credentialed paths"
    empty-title="No sessions"
    empty-description="No session records match the current dashboard state."
    maximize-label="Expand session table"
    modal-description="Bootstrap, credential, strict, and sticky state records."
    scroll-class="dashboard-table-scroll--sessions"
    modal-scroll-class="dashboard-table-scroll--sessions-modal"
    :scroll-test-id="scrollTestId"
    :modal-scroll-test-id="modalScrollTestId"
    :ui="{
      root: 'dashboard-table dashboard-table--sessions min-w-[1140px]',
      th: 'whitespace-nowrap',
      td: 'whitespace-nowrap'
    }"
    sticky="header"
  >
    <template #id-cell="{ row }">
      <SessionId :value="row.original.id" max-class="max-w-[8rem]" />
    </template>
    <template #status-cell="{ row }">
      <StatusBadge :status="sessionStatus(row.original)" />
    </template>
    <template #appId-cell="{ row }">
      <ClippedValue :value="row.original.appId" max-class="max-w-[13rem] text-[0.69rem]" title="App id" />
    </template>
    <template #requestedCountry-cell="{ row }">
      <span v-if="row.original.requestedCountry" class="dashboard-mono">
        {{ row.original.requestedCountry }}
      </span>
      <NullBadge v-else field="country" />
    </template>
    <template #requestedProfile-cell="{ row }">
      <span>{{ row.original.requestedProfile ?? "none" }}</span>
    </template>
    <template #activeCredentialKid-cell="{ row }">
      <CredentialId :value="row.original.activeCredentialKid" max-class="max-w-[8rem]" title="Credential kid" />
    </template>
    <template #credentialExpiresAt-cell="{ row }">
      <UTooltip v-if="hasCredentialExpiry(row.original)" :text="credentialExpiryTooltip(row.original)">
        <StatusBadge v-if="credentialExpired(row.original)" status="expired" />
        <span v-else>{{ credentialExpiryLabel(row.original) }}</span>
      </UTooltip>
      <NullBadge v-else field="credential expiry" />
    </template>
    <template #lastSeenAt-cell="{ row }">
      <span>{{ formatRelative(row.original.lastSeenAt, now) }}</span>
    </template>
    <template #strict-cell="{ row }">
      <StatusBadge :status="row.original.strict ? 'strict' : 'off'" />
    </template>
    <template #stickyHashPresent-cell="{ row }">
      <StatusBadge :status="row.original.stickyHashPresent ? 'sticky' : 'off'" />
    </template>
  </SortableTable>
</template>
